import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import newsArticles from '../data/newsData';
import SEO from '../components/SEO';

const NewsArticle = () => {
  const { slug } = useParams();
  const article = newsArticles.find(a => a.id === slug);
  const currentIndex = newsArticles.findIndex(a => a.id === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <Link to="/resources/news" className="text-blue-600 font-semibold hover:underline">Back to News</Link>
        </div>
      </div>
    );
  }

  const prevArticle = currentIndex > 0 ? newsArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < newsArticles.length - 1 ? newsArticles[currentIndex + 1] : null;

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-slate-900 mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-xl font-bold text-slate-800 mt-6 mb-3">{trimmed.replace('### ', '')}</h3>;
      if (trimmed.startsWith('- ')) return <li key={i} className="text-slate-600 leading-relaxed ml-6 mb-2 list-disc">{trimmed.replace('- ', '')}</li>;
      return <p key={i} className="text-slate-600 leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="news-article-page">
      <SEO title={article.title} description={article.excerpt || article.title} path={`/resources/news/${article.id}`} />
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources/news" className="inline-flex items-center text-blue-300 hover:text-white mb-6 transition-colors" data-testid="back-to-news">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to News
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-blue-500/30 text-blue-200 text-sm font-semibold px-4 py-1.5 rounded-full border border-blue-400/30">{article.category}</span>
            <span className="text-blue-300 text-sm">{article.source}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight">{article.title}</h1>
          <div className="text-blue-200">{article.date}</div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
        <img src={article.image} alt={article.title} className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl" />
      </div>

      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent(article.content)}
        </div>
      </article>

      <section className="border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevArticle ? (
              <Link to={`/resources/news/${prevArticle.id}`} className="group flex items-center gap-3 text-left">
                <ArrowLeft className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                <div>
                  <div className="text-xs text-slate-500 mb-1">Previous</div>
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors max-w-xs truncate">{prevArticle.title}</div>
                </div>
              </Link>
            ) : <div />}
            {nextArticle ? (
              <Link to={`/resources/news/${nextArticle.id}`} className="group flex items-center gap-3 text-right">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Next</div>
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors max-w-xs truncate">{nextArticle.title}</div>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle;
